import { ClerkProvider, SignInButton, UserButton, useAuth, useUser } from "@clerk/clerk-react"
import { type ReactNode, useEffect, useState } from "react"
import { adminKeys } from "../../search-standalone/services/adminKeys"

// Search Admin signs in with in5.app's Clerk instance (the same organizations as in5.app).
// Without VITE_CLERK_PUBLISHABLE_KEY the app runs on admin keys only.
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || ""
export const CLERK_ENABLED = Boolean(PUBLISHABLE_KEY)

/** Hands the signed-in person's session token to adminKeys, so every admin request carries it. */
function ClerkBridge() {
	const { isSignedIn, getToken } = useAuth()
	useEffect(() => {
		adminKeys.setSession(isSignedIn ? () => getToken() : null)
		return () => adminKeys.setSession(null)
	}, [isSignedIn, getToken])
	return null
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
	if (!CLERK_ENABLED) return <>{children}</>
	return (
		<ClerkProvider publishableKey={PUBLISHABLE_KEY}>
			<ClerkBridge />
			{children}
		</ClerkProvider>
	)
}

/** Whether someone is signed in, and whether the server treats them as an operator. */
export function useAdminSession() {
	const [state, setState] = useState({ signedIn: adminKeys.signedIn(), operator: false })
	useEffect(() => {
		let live = true
		const load = async () => {
			if (!adminKeys.signedIn()) return live && setState({ signedIn: false, operator: false })
			try {
				const backend = import.meta.env.VITE_BACKEND_URL || ""
				const response = await fetch(`${backend}/api/v1/search/operator/session`, {
					headers: await adminKeys.authHeaders(),
				})
				const result = await response.json()
				if (live) setState({ signedIn: Boolean(result.signedIn), operator: Boolean(result.operator) })
			} catch {
				if (live) setState({ signedIn: true, operator: false })
			}
		}
		void load()
		const stop = adminKeys.subscribe(() => void load())
		return () => {
			live = false
			stop()
		}
	}, [])
	return state
}

/** "Sign in with in5", or who is signed in and what they can manage. */
export function ClerkAccount() {
	const { isSignedIn, user } = useUser()
	const session = useAdminSession()
	if (!isSignedIn)
		return (
			<div className="flex items-center gap-3">
				<SignInButton mode="modal">
					<button className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm" type="button">
						Sign in with in5
					</button>
				</SignInButton>
				<span className="text-xs text-muted-foreground">
					Store admins manage their own store; operators manage every store.
				</span>
			</div>
		)
	return (
		<div className="flex items-center gap-3 text-sm">
			<UserButton />
			<span>
				{user?.primaryEmailAddress?.emailAddress || user?.fullName}
				<span className="text-xs text-muted-foreground">
					{" "}
					· {session.operator ? "Operator: every store" : "Store admin for stores in your in5 organizations"}
				</span>
			</span>
		</div>
	)
}
